import { useState } from "react";
import { motion } from "framer-motion";
import { Send, MapPin, Phone, Mail, Clock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      if (typeof window.fbq === 'function') window.fbq('track', 'Lead');
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (err) {
      toast.error("Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative py-32 sm:py-40 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} className="industrial-line mb-8" />
            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Contact <span className="gradient-text">Us</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base sm:text-lg text-[#A3A3A3] max-w-xl leading-relaxed">
              Get in touch with our team for enquiries, technical consultations, or to discuss 
              your next industrial textile project.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16"
                >
                  <div className="w-16 h-16 bg-[#FF5722]/10 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={32} className="text-[#FF5722]" />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                    Message Sent
                  </h3>
                  <p className="text-[#A3A3A3] mb-6">We'll get back to you shortly.</p>
                  <button
                    data-testid="contact-send-another"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
                    }}
                    className="bg-[#FF5722] text-white px-6 py-3 font-semibold text-sm hover:bg-[#E64A19] transition-colors"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  onSubmit={handleSubmit}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
                  className="space-y-6"
                >
                  <motion.div variants={fadeUp}>
                    <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                      Send Us a Message
                    </h2>
                    <p className="text-sm text-[#A3A3A3] mb-6">
                      Fill in the form below and we'll respond within 24 hours.
                    </p>
                  </motion.div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <motion.div variants={fadeUp}>
                      <Label htmlFor="c-name" className="text-sm font-medium text-[#A3A3A3] mb-2 block">Full Name *</Label>
                      <Input
                        id="c-name"
                        name="name"
                        data-testid="contact-name-input"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="John Smith"
                        className="bg-[#1F1F1F] border-[#2E2E2E] text-white placeholder:text-[#666] focus:ring-1 focus:ring-[#FF5722] focus:border-[#FF5722] rounded-none h-12"
                      />
                    </motion.div>
                    <motion.div variants={fadeUp}>
                      <Label htmlFor="c-email" className="text-sm font-medium text-[#A3A3A3] mb-2 block">Email *</Label>
                      <Input
                        id="c-email"
                        name="email"
                        type="email"
                        data-testid="contact-email-input"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com.au"
                        className="bg-[#1F1F1F] border-[#2E2E2E] text-white placeholder:text-[#666] focus:ring-1 focus:ring-[#FF5722] focus:border-[#FF5722] rounded-none h-12"
                      />
                    </motion.div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <motion.div variants={fadeUp}>
                      <Label htmlFor="c-phone" className="text-sm font-medium text-[#A3A3A3] mb-2 block">Phone</Label>
                      <Input
                        id="c-phone"
                        name="phone"
                        type="tel"
                        data-testid="contact-phone-input"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="(08) 9XXX XXXX"
                        className="bg-[#1F1F1F] border-[#2E2E2E] text-white placeholder:text-[#666] focus:ring-1 focus:ring-[#FF5722] focus:border-[#FF5722] rounded-none h-12"
                      />
                    </motion.div>
                    <motion.div variants={fadeUp}>
                      <Label htmlFor="c-subject" className="text-sm font-medium text-[#A3A3A3] mb-2 block">Subject</Label>
                      <Input
                        id="c-subject"
                        name="subject"
                        data-testid="contact-subject-input"
                        value={form.subject}
                        onChange={handleChange}
                        placeholder="General Enquiry"
                        className="bg-[#1F1F1F] border-[#2E2E2E] text-white placeholder:text-[#666] focus:ring-1 focus:ring-[#FF5722] focus:border-[#FF5722] rounded-none h-12"
                      />
                    </motion.div>
                  </div>

                  <motion.div variants={fadeUp}>
                    <Label htmlFor="c-message" className="text-sm font-medium text-[#A3A3A3] mb-2 block">Message *</Label>
                    <textarea
                      id="c-message"
                      name="message"
                      data-testid="contact-message-input"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="How can we help you?"
                      className="w-full bg-[#1F1F1F] border border-[#2E2E2E] text-white placeholder:text-[#666] focus:ring-1 focus:ring-[#FF5722] focus:border-[#FF5722] rounded-none p-3 text-sm resize-none outline-none"
                    />
                  </motion.div>

                  <motion.div variants={fadeUp}>
                    <button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={loading}
                      className="inline-flex items-center gap-2 bg-[#FF5722] text-white px-8 py-3.5 font-semibold text-sm hover:bg-[#E64A19] transition-colors disabled:opacity-50"
                    >
                      {loading ? "Sending..." : (
                        <>
                          <Send size={16} />
                          Send Message
                        </>
                      )}
                    </button>
                  </motion.div>
                </motion.form>
              )}
            </div>

            {/* Info + Map */}
            <div className="space-y-8">
              {/* Contact Info */}
              <div className="bg-[#141414] border border-[#2E2E2E] p-8">
                <h3 className="text-lg font-bold mb-6" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                  Contact Details
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FF5722]/10 flex items-center justify-center shrink-0">
                      <MapPin size={18} className="text-[#FF5722]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">Address</p>
                      <p className="text-sm text-[#A3A3A3]">8 Blamey Place<br />O'Connor, WA 6163<br />Australia</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FF5722]/10 flex items-center justify-center shrink-0">
                      <Phone size={18} className="text-[#FF5722]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">Phone</p>
                      <p className="text-sm text-[#A3A3A3]">+61 413 136 160</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FF5722]/10 flex items-center justify-center shrink-0">
                      <Mail size={18} className="text-[#FF5722]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">Email</p>
                      <p className="text-sm text-[#A3A3A3]">office@ngindustries.com.au</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FF5722]/10 flex items-center justify-center shrink-0">
                      <Clock size={18} className="text-[#FF5722]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">Business Hours</p>
                      <p className="text-sm text-[#A3A3A3]">
                        Monday - Friday: 7:00 AM - 4:30 PM<br />
                        Saturday: By Appointment<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div data-testid="contact-map" className="bg-[#141414] border border-[#2E2E2E] overflow-hidden">
                <iframe
                  title="NG Industries Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3388.1!2d115.79!3d-32.06!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2a32973e9a7b8b9d%3A0x1!2s8+Blamey+Pl%2C+O'Connor+WA+6163!5e0!3m2!1sen!2sau!4v1700000000000!5m2!1sen!2sau"
                  width="100%"
                  height="300"
                  style={{ border: 0, filter: "invert(90%) hue-rotate(180deg)" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
